const TOKEN_KEY = 'sk_admin_token';
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public readonly errorCode: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(errorCode);
    this.name = 'ApiError';
  }
}

export interface ApiInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

// Single-flight: N requests que tomam 401 ao mesmo tempo disparam UM refresh.
let refreshInFlight: Promise<boolean> | null = null;

export function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST' });
        if (!res.ok) return false;
        const body = (await res.json()) as { token?: string };
        if (typeof body.token !== 'string' || !body.token) return false;
        setToken(body.token);
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

export async function api<T = unknown>(path: string, init: ApiInit = {}): Promise<T> {
  const { body, query, headers: initHeaders, ...rest } = init;

  let url = `${API_BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }

  const doFetch = async (): Promise<Response> => {
    const token = getToken();
    const headers = new Headers(initHeaders);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (body !== undefined && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return fetch(url, {
      ...rest,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let res = await doFetch();

  // Access token expirou no meio da sessão → renova via cookie e repete UMA
  // vez. Rotas de auth ficam de fora pra não virar loop.
  if (res.status === 401 && !path.startsWith('/auth/')) {
    const refreshed = await refreshAccessToken();
    if (refreshed) res = await doFetch();
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const responseBody = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const errorCode =
      isJson && typeof (responseBody as { error?: string }).error === 'string'
        ? (responseBody as { error: string }).error
        : `http_${res.status}`;
    throw new ApiError(errorCode, res.status, responseBody);
  }

  return responseBody as T;
}
